import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Card, Spin, Table, Tabs } from "antd";
import { CalendarOutlined, DoubleLeftOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import moment from "moment";
import KycService from "../../services/kyc-service";
import KycMaterialsTab from "./KycMaterialsTab";
import KycPartnerQuotationTab from "./KycPartnerQuotationTab";
import KycPartnerOrdersTab from "./KycPartnerOrdersTab";
import KycPartnerDesignTab from "./KycPartnerDesignTab";
import {
	buildKycCompanyTabUrl,
	buildKycDashboardReturnLink,
	buildKycTileCountsPayloadWithScope,
	getContactTileCounts,
	getMaterialTileCounts,
	getDesignTileCounts,
	getCylinderTileCounts,
	KYC_DEFAULT_PARTNER_DETAILS_TAB,
	KYC_PARTNER_DETAILS_TAB_PARAM,
	loadKycPartnerNavContext,
	resolvePartnerDetailsTab,
	saveKycPartnerNavContext,
	getKycAppliedFilterPayload,
	saveKycGlobalFilterPayload,
} from "./kycHelpers";
import "./KYC-Dashboard.css";
import {
	KYCGroupCompanyImage,
	KYCContactsImage,
	KYCCylindersImage,
	KYCDesignImage,
	KYCMaterialsImage,
	KYCBusinessOverviewImage,
} from "../Elite/imageAssets";

const { TabPane } = Tabs;

const defaultPayload = {
	fromDate: "2026-04-09T12:10:21.201Z",
	toDate: "2026-04-09T12:10:21.201Z",
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const createdOnFormats = [
	"MM/DD/YYYY HH:mm:ss",
	"MM/DD/YYYY H:mm:ss",
	"M/D/YYYY HH:mm:ss",
	"M/D/YYYY H:mm:ss",
	"M/D/YYYY h.mm.ss A",
	"M/D/YYYY h:mm:ss A",
	moment.ISO_8601,
];

const formatDate = (value) => {
	if (!value) {
		return "-";
	}
	const parsedDate = moment(value, createdOnFormats, true);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "-";
};

const parseCreatedOnDate = (value) => moment(value, createdOnFormats, true);

const parseOnboardDateValue = (value) => {
	if (value === undefined || value === null || value === "") {
		return null;
	}
	const parsedDate = moment(value, createdOnFormats, true);
	return parsedDate.isValid() ? parsedDate : null;
};

const getAssociatedYearsValue = (record = {}) => {
	const rawValue = [record?.associated_Years, record?.associatedYears, record?.associationYear, record?.association_Year].find(
		(value) => value !== undefined && value !== null && value !== ""
	);
	if (rawValue === undefined || rawValue === null || rawValue === "") {
		return "";
	}
	const displayValue = String(rawValue).trim();
	if (!displayValue) {
		return "";
	}
	if (/year/i.test(displayValue)) {
		return displayValue;
	}
	const numericValue = Number(displayValue);
	if (!Number.isNaN(numericValue)) {
		return `${displayValue} ${numericValue === 1 ? "year" : "years"}`;
	}
	return displayValue;
};

const getPartnerHeaderMeta = (rows = []) => {
	const list = Array.isArray(rows) ? rows : [];
	const source =
		list.find(
			(row) =>
				row?.onboardDate ||
				row?.associated_Years ||
				row?.associatedYears ||
				row?.associationYear
		) ||
		list[0] ||
		{};
	return {
		onboardDate: parseOnboardDateValue(source?.onboardDate)?.format("DD-MM-YYYY") || "",
		associatedYears: getAssociatedYearsValue(source),
	};
};

const getValue = (record, keys) => {
	for (const key of keys) {
		const value = record?.[key];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}
	return "-";
};

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const PARTNER_FUNCTION_ACCOUNT_KEY_PREFIX = "kyc-partner-function-account:";

const PARTNER_DETAIL_TAB_LABELS = {
	partnerFunction: "Partner Function",
	material: "Material",
	quotation: "Quotation",
	orders: "Orders",
	design: "Design",
	creditDetail: "Credit Detail",
	cardiogram: "Cardiogram",
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const parseMaterialListResponse = (responseData) => {
	const materialList = Array.isArray(responseData)
		? responseData
		: responseData?.kycMaterialMasterLists ||
			responseData?.kycMaterialMasterList ||
			responseData?.materialMasterList ||
			responseData?.data ||
			[];

	return {
		rows: Array.isArray(materialList) ? materialList : [],
		totalRecords: Number(responseData?.totalRecords || materialList.length || 0),
		currentPage: Number(responseData?.currentPage || 1),
	};
};

const KycPartnerFunctionDetails = () => {
	const { crmGuid } = useParams();
	const location = useLocation();
	const loginDetails = useSelector(
		(state) => state.loginReducer.loginDetials.userDetails
	);
	const [searchParams, setSearchParams] = useSearchParams();
	const [isLoading, setIsLoading] = useState(false);
	const [isCardsLoading, setIsCardsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState(() =>
		resolvePartnerDetailsTab(searchParams.get(KYC_PARTNER_DETAILS_TAB_PARAM))
	);
	const [partnerFunctionRows, setPartnerFunctionRows] = useState([]);
	const [tableSorter, setTableSorter] = useState({ field: "", order: "" });
	const [partnerFunctionPagination, setPartnerFunctionPagination] = useState({
		currentPage: 1,
		pageSize: 10,
	});
	const [partnerFunctionCount, setPartnerFunctionCount] = useState({});
	const [contactsCount, setContactsCount] = useState({});
	const [materialsCount, setMaterialsCount] = useState({});
	const [materialsTable, setMaterialsTable] = useState({
		rows: [],
		totalRecords: 0,
		currentPage: 1,
		pageSize: 10,
	});
	const [materialsSorter, setMaterialsSorter] = useState({
		field: "",
		order: "",
	});
	const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
	const [designsCount, setDesignsCount] = useState({});
	const [cylindersCount, setCylindersCount] = useState({});
	const [businessOverviewCount, setBusinessOverviewCount] = useState({});

	const navContext = useMemo(() => {
		const fromState = location.state || {};
		const stored = loadKycPartnerNavContext(crmGuid);
		return {
			groupCompanyId: fromState.groupCompanyId || stored.groupCompanyId || "",
			groupCompanyName: fromState.groupCompanyName || stored.groupCompanyName || "",
			accountName: fromState.accountName || stored.accountName || "",
			returnTab: fromState.returnTab || stored.returnTab || "account",
			materialsCustomer: fromState.materialsCustomer || stored.materialsCustomer || "",
		};
	}, [crmGuid, location.state]);

	const kycFilterPayload = useMemo(
		() => getKycAppliedFilterPayload(loginDetails),
		[loginDetails]
	);

	useEffect(() => {
		saveKycGlobalFilterPayload(kycFilterPayload);
	}, [kycFilterPayload]);

	useEffect(() => {
		if (!crmGuid) {
			return;
		}

		const { groupCompanyId, groupCompanyName, accountName, returnTab, materialsCustomer } = navContext;
		if (!accountName && !groupCompanyName && !materialsCustomer) {
			return;
		}

		saveKycPartnerNavContext(crmGuid, {
			groupCompanyId,
			groupCompanyName,
			accountName,
			returnTab,
			materialsCustomer,
		});

		if (accountName) {
			sessionStorage.setItem(`${PARTNER_FUNCTION_ACCOUNT_KEY_PREFIX}${crmGuid}`, accountName);
		}
	}, [crmGuid, navContext]);

	useEffect(() => {
		const resolvedTab = resolvePartnerDetailsTab(searchParams.get(KYC_PARTNER_DETAILS_TAB_PARAM));
		setActiveTab((prev) => (resolvedTab !== prev ? resolvedTab : prev));
	}, [searchParams]);

	const soldToPartyName = useMemo(() => {
		if (navContext.accountName) {
			return navContext.accountName;
		}
		if (!crmGuid) {
			return "";
		}
		return sessionStorage.getItem(`${PARTNER_FUNCTION_ACCOUNT_KEY_PREFIX}${crmGuid}`) || "";
	}, [crmGuid, navContext.accountName]);

	const groupCompanyBreadcrumbLabel = (navContext.groupCompanyName || "").trim();
	const accountBreadcrumbLabel = (soldToPartyName || "").trim();
	const normalizedGroupLabel = groupCompanyBreadcrumbLabel.toLowerCase();
	const normalizedAccountLabel = accountBreadcrumbLabel.toLowerCase();
	const hasDistinctGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel !== normalizedAccountLabel;
	const hasDuplicateGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel === normalizedAccountLabel;

	const showGroupInBreadcrumb = Boolean(
		groupCompanyBreadcrumbLabel &&
			(navContext.returnTab === "materials" || hasDistinctGroupAndAccount)
	);
	const showAccountInBreadcrumb = Boolean(
		accountBreadcrumbLabel &&
			navContext.returnTab === "account" &&
			(hasDistinctGroupAndAccount ||
				hasDuplicateGroupAndAccount ||
				!groupCompanyBreadcrumbLabel)
	);

	const materialsBreadcrumbLabel = navContext.materialsCustomer.trim();
	const hasMaterialsBreadcrumb = Boolean(materialsBreadcrumbLabel);
	const showMaterialsInBreadcrumb =
		navContext.returnTab === "materials" && hasMaterialsBreadcrumb;

	const activeTabBreadcrumbLabel =
		PARTNER_DETAIL_TAB_LABELS[activeTab] || "Partner Function";

	const dashboardGroupCompanyLink = useMemo(() => {
		if (!showGroupInBreadcrumb) {
			return "/kyc";
		}

		return buildKycDashboardReturnLink({
			returnTab: "groupCompany",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
		});
	}, [showGroupInBreadcrumb, groupCompanyBreadcrumbLabel, navContext.groupCompanyId]);

	const dashboardAccountLink = useMemo(() => {
		if (!showAccountInBreadcrumb) {
			return buildKycCompanyTabUrl("account");
		}

		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
			accountName: accountBreadcrumbLabel,
		});
	}, [
		showAccountInBreadcrumb,
		accountBreadcrumbLabel,
		groupCompanyBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const dashboardMaterialsDrillAccountLink = useMemo(() => {
		if (!hasMaterialsBreadcrumb) {
			return null;
		}

		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: showGroupInBreadcrumb ? groupCompanyBreadcrumbLabel : "",
			accountName: materialsBreadcrumbLabel,
		});
	}, [
		hasMaterialsBreadcrumb,
		showGroupInBreadcrumb,
		groupCompanyBreadcrumbLabel,
		materialsBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const dashboardBackLink = useMemo(
		() =>
			buildKycDashboardReturnLink({
				returnTab: navContext.returnTab,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				accountName: soldToPartyName,
				materialsCustomer:
					navContext.returnTab === "materials"
						? materialsBreadcrumbLabel || soldToPartyName
						: navContext.materialsCustomer,
			}),
		[navContext, soldToPartyName, materialsBreadcrumbLabel]
	);

	const materialsCustomerFilter = useMemo(() => {
		if (!soldToPartyName || soldToPartyName === "Sold to Party Name") {
			return "";
		}
		return String(soldToPartyName).trim();
	}, [soldToPartyName]);

	const getCardTitle = (countObj, label, keys = ["total"]) => {
		const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
		return countValue !== undefined ? `${countValue} ${label}` : label;
	};

	const getSortOrder = (tableSorter, columnKey) => {
		if (tableSorter.field !== columnKey) {
			return null;
		}

		return tableSorter.order === "ASC" ? "ascend" : tableSorter.order === "DESC" ? "descend" : null;
	};

	const handleTableAscDes = (pagination, filters, sorter) => {
		const sortField = sorter?.field || sorter?.columnKey;
		const hasSortChange = Boolean(sortField && sorter?.order);

		if (hasSortChange) {
			const nextField = sortField;
			const nextOrder = sorter.order === "ascend" ? "ASC" : "DESC";

			if (nextField !== tableSorter.field || nextOrder !== tableSorter.order) {
				setPartnerFunctionPagination((prev) => ({
					...prev,
					currentPage: 1,
				}));
				setTableSorter({ field: nextField, order: nextOrder });
			}
			return;
		}

		setPartnerFunctionPagination((prev) => ({
			currentPage: pagination?.current ?? prev.currentPage,
			pageSize: pagination?.pageSize ?? prev.pageSize,
		}));
	};

	const loadPartnerFunctionList = useCallback(() => {
		if (!crmGuid) {
			return;
		}

		setIsLoading(true);
		KycService.getPartnerFunctionByCompanyGuidAPI(crmGuid)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (!success) {
					NotificationManager.error(response.data?.message || "Unable to load partner function details.");
					setPartnerFunctionRows([]);
					setPartnerFunctionPagination({ currentPage: 1, pageSize: 10 });
					return;
				}

				const responseData = response.data?.responseData || {};
				const rows = Array.isArray(responseData)
					? responseData
					: responseData.partnerFunctionDetails || responseData.partnerFunctions || responseData.data || [];
				setPartnerFunctionRows(Array.isArray(rows) ? rows : []);
				setPartnerFunctionPagination({ currentPage: 1, pageSize: 10 });
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load partner function details.");
				setPartnerFunctionRows([]);
				setPartnerFunctionPagination({ currentPage: 1, pageSize: 10 });
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [crmGuid]);

	useEffect(() => {
		if (!crmGuid) {
			return;
		}
		loadPartnerFunctionList();
	}, [crmGuid, loadPartnerFunctionList]);

	useEffect(() => {
		if (!crmGuid) {
			return;
		}

		setIsCardsLoading(true);
		KycService.getKycTileCountsAPI(
			buildKycTileCountsPayloadWithScope(
				{
					...defaultPayload,
					...kycFilterPayload,
					crmGuid,
				},
				{
					isplCustomer: materialsCustomerFilter,
				}
			)
		)
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};
					setPartnerFunctionCount(apiData.partnerFunction ? apiData.partnerFunction : {});
					setContactsCount(getContactTileCounts(apiData));
					setMaterialsCount(getMaterialTileCounts(apiData));
					setDesignsCount(getDesignTileCounts(apiData));
					setCylindersCount(getCylinderTileCounts(apiData));
					setBusinessOverviewCount(apiData.businessOverview ? apiData.businessOverview : {});
				}
			})
			.catch((error) => {
				console.log(error, "Error");
			})
			.finally(() => {
				setIsCardsLoading(false);
			});
	}, [crmGuid, kycFilterPayload, materialsCustomerFilter]);

	useEffect(() => {
		setMaterialsTable({
			rows: [],
			totalRecords: 0,
			currentPage: 1,
			pageSize: 10,
		});
	}, [crmGuid]);

	const loadMaterialsList = useCallback(() => {
		if (!crmGuid) {
			return;
		}

		setIsMaterialsLoading(true);
		const requestData = {
			...defaultPayload,
			...kycFilterPayload,
			crmGuid,
			CrmGuid: crmGuid,
			pageindex: materialsTable.currentPage,
			pagesize: materialsTable.pageSize,
			sortcolumn: materialsSorter.field || "",
			orderBy: materialsSorter.order || "",
			search: "",
			ispl_customer: materialsCustomerFilter,
		};

		KycService.getKycMaterialMasterListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (!success) {
					NotificationManager.error(
						response.data?.message || "Unable to load materials listing."
					);
					setMaterialsTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
					return;
				}

				const { rows, totalRecords, currentPage } = parseMaterialListResponse(
					response.data.responseData || {}
				);

				setMaterialsTable((prev) => ({
					...prev,
					rows,
					totalRecords,
					currentPage,
				}));
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load materials listing.");
				setMaterialsTable((prev) => ({ ...prev, rows: [], totalRecords: 0 }));
			})
			.finally(() => {
				setIsMaterialsLoading(false);
			});
	}, [
		crmGuid,
		kycFilterPayload,
		materialsCustomerFilter,
		materialsTable.currentPage,
		materialsTable.pageSize,
		materialsSorter.field,
		materialsSorter.order,
	]);

	useEffect(() => {
		if (activeTab !== "material" || !crmGuid) {
			return;
		}
		loadMaterialsList();
	}, [activeTab, crmGuid, loadMaterialsList]);

	const handleTabChange = (key) => {
		const resolvedTab = resolvePartnerDetailsTab(key);
		setActiveTab(resolvedTab);
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (!resolvedTab || resolvedTab === KYC_DEFAULT_PARTNER_DETAILS_TAB) {
					next.delete(KYC_PARTNER_DETAILS_TAB_PARAM);
				} else {
					next.set(KYC_PARTNER_DETAILS_TAB_PARAM, resolvedTab);
				}
				return next;
			},
			{ replace: true }
		);
	};

	const handleMaterialsTableChange = ({ currentPage, sortColumn, orderBy, pageSize }) => {
		setMaterialsTable((prev) => ({
			...prev,
			currentPage: currentPage || 1,
			pageSize: pageSize || prev.pageSize,
		}));
		setMaterialsSorter((prev) => ({
			field: sortColumn || prev.field,
			order: orderBy || prev.order,
		}));
	};

	const sortedPartnerFunctionRows = useMemo(() => {
		if (!tableSorter.field || !tableSorter.order) {
			return partnerFunctionRows;
		}

		const sortedRows = [...partnerFunctionRows];
		sortedRows.sort((leftRow, rightRow) => {
			if (tableSorter.field === "createdOn") {
				const leftDate = parseCreatedOnDate(leftRow.createdOn);
				const rightDate = parseCreatedOnDate(rightRow.createdOn);
				const leftValue = leftDate.isValid() ? leftDate.valueOf() : 0;
				const rightValue = rightDate.isValid() ? rightDate.valueOf() : 0;
				return tableSorter.order === "ASC" ? leftValue - rightValue : rightValue - leftValue;
			}

			const leftValue = String(leftRow?.[tableSorter.field] || "").toLowerCase();
			const rightValue = String(rightRow?.[tableSorter.field] || "").toLowerCase();

			if (leftValue === rightValue) {
				return 0;
			}

			if (tableSorter.order === "ASC") {
				return leftValue > rightValue ? 1 : -1;
			}

			return leftValue < rightValue ? 1 : -1;
		});

		return sortedRows;
	}, [partnerFunctionRows, tableSorter]);

	const paginatedPartnerFunctionRows = useMemo(() => {
		const { currentPage, pageSize } = partnerFunctionPagination;
		const startIndex = (currentPage - 1) * pageSize;
		return sortedPartnerFunctionRows.slice(startIndex, startIndex + pageSize);
	}, [sortedPartnerFunctionRows, partnerFunctionPagination]);

	const partnerHeaderMeta = useMemo(
		() => getPartnerHeaderMeta(partnerFunctionRows),
		[partnerFunctionRows]
	);

	const tableColumns = useMemo(
		() => [
			{
				title: "SAP Code",
				dataIndex: "ispl_SapCode",
				key: "ispl_SapCode",
				sorter: true,
				field: "ispl_SapCode",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_SapCode"),
				width: 160,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Type (New)",
				dataIndex: "ispl_PType",
				key: "ispl_PType",
				sorter: true,
				field: "ispl_PType",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_PType"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Account Name",
				dataIndex: "ispl_Name",
				key: "ispl_Name",
				sorter: true,
				field: "ispl_Name",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_Name"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Street 1",
				dataIndex: "ispl_Street1",
				key: "ispl_Street1",
				sorter: true,
				field: "ispl_Street1",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_Street1"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Street 2",
				dataIndex: "ispl_Street2",
				key: "ispl_Street2",
				sorter: true,
				field: "ispl_Street2",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_Street2"),
				width: 220,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "City",
				dataIndex: "ispl_CityName",
				key: "ispl_CityName",
				sorter: true,
				field: "ispl_CityName",
				showSorterTooltip: false,
				sortOrder: getSortOrder(tableSorter, "ispl_CityName"),
				width: 140,
				render: (value) => <span>{value || "-"}</span>,
			},
			{
				title: "Created On",
				dataIndex: "createdOn",
				sorter: true,
				field: "createdOn",
				showSorterTooltip: false,
				key: "createdOn",
				sortOrder: getSortOrder(tableSorter, "createdOn"),
				width: 140,
				render: (value) => <span>{formatDate(value)}</span>,
			},
		],
		[tableSorter],
	);

	const partnerFunctionStats = useMemo(() => {
		const counts = { shipToParty: 0, billToParty: 0, payer: 0 };
		partnerFunctionRows.forEach((row) => {
			const typeValue = String(
				getValue(row, ["typeNew", "type", "partnerType", "ispl_PType", "ispl_Type"]),
			).toLowerCase();
			if (typeValue.includes("ship to party")) counts.shipToParty += 1;
			if (typeValue.includes("bill to party")) counts.billToParty += 1;
			if (typeValue.includes("payer")) counts.payer += 1;
		});
		return counts;
	}, [partnerFunctionRows]);

	const partnerFunctionTileStats = {
		total: getFirstAvailableValue(
			partnerFunctionCount?.partnerTotalCount,
			partnerFunctionCount?.totalCount,
			partnerFunctionCount?.total,
			partnerFunctionRows.length,
		),
		shipToParty: getFirstAvailableValue(
			partnerFunctionCount?.ship_To_Party,
			partnerFunctionStats.shipToParty,
		),
		billToParty: getFirstAvailableValue(
			partnerFunctionCount?.bill_To_Party,
			partnerFunctionStats.billToParty,
		),
		payer: getFirstAvailableValue(partnerFunctionCount?.payer, partnerFunctionStats.payer),
	};

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								{showGroupInBreadcrumb ? (
									<Breadcrumb.Item>
										<Link to={dashboardGroupCompanyLink}>
											<label>{groupCompanyBreadcrumbLabel}</label>
										</Link>
									</Breadcrumb.Item>
								) : null}
								{showMaterialsInBreadcrumb ? (
									<Breadcrumb.Item>
										<Link to={dashboardMaterialsDrillAccountLink}>
											<label>{materialsBreadcrumbLabel}</label>
										</Link>
									</Breadcrumb.Item>
								) : null}
								{showAccountInBreadcrumb ? (
									<Breadcrumb.Item>
										<Link to={dashboardAccountLink}>
											<label>{accountBreadcrumbLabel}</label>
										</Link>
									</Breadcrumb.Item>
								) : null}
								<Breadcrumb.Item>
									<label>{activeTabBreadcrumbLabel}</label>
								</Breadcrumb.Item>
							</Breadcrumb>
							<div className="kyc-partner-top-actions">
								<div className="kyc-partner-meta-field">
									<label className="kyc-partner-meta-label">Onboard Date</label>
									<div className="kyc-partner-meta-value" title="Onboard Date">
										<span>{partnerHeaderMeta.onboardDate || "-"}</span>
										<CalendarOutlined className="kyc-partner-meta-icon" />
									</div>
								</div>
								<div className="kyc-partner-meta-field">
									<label className="kyc-partner-meta-label">Association Year</label>
									<div className="kyc-partner-meta-value" title="Year">
										<span>{partnerHeaderMeta.associatedYears || "-"}</span>
										<CalendarOutlined className="kyc-partner-meta-icon" />
									</div>
								</div>
								<Link to={dashboardBackLink}>
									<Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
										Back
									</Button>
								</Link>
							</div>
						</div>
						<Spin size="large" spinning={isLoading || isCardsLoading}>
							<div className="row">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_green2">
										<div className="card-content">
											<div className="kyc-card-body KYCMaterials images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCMaterialsImage} alt="Img" />
														<span>{partnerFunctionTileStats.total || 0} Partner Function</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{partnerFunctionTileStats.shipToParty || "-"}
														</h5>
														<h6 className="text-white mb-0">Ship to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{partnerFunctionTileStats.billToParty || "-"}
														</h5>
														<h6 className="text-white mb-0">Bill to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{partnerFunctionTileStats.payer || "-"}
														</h5>
														<h6 className="text-white mb-0">Payer</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_red">
										<div className="card-content">
											<div className="kyc-card-body KYCContacts images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCContactsImage} alt="Img" />
														<span>
															{getCardTitle(contactsCount, "Contacts", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{contactsCount.activeCount ??
																contactsCount.active ??
																contactsCount.running ??
																"-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{contactsCount.inactiveCount ??
																contactsCount.hide ??
																"-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_blue">
										<div className="card-content">
											<div className="kyc-card-body KYCMaterials images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCMaterialsImage} alt="Img" />
														<span>
															{getCardTitle(materialsCount, "Materials", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getFirstAvailableValue(
																materialsCount?.runningCount,
																materialsCount?.running
															) ?? "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getFirstAvailableValue(
																materialsCount?.hiddenCount,
																materialsCount?.hide
															) ?? "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_grey">
										<div className="card-content">
											<div className="kyc-card-body KYCDesign images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCDesignImage} alt="Img" />
														<span>
															{getCardTitle(designsCount, "Designs", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getFirstAvailableValue(
																designsCount?.runningCount,
																designsCount?.running
															) ?? "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getFirstAvailableValue(
																designsCount?.hiddenCount,
																designsCount?.hide
															) ?? "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple">
										<div className="card-content">
											<div className="kyc-card-body KYCCylinders images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCCylindersImage} alt="Img" />
														<span>
															{getCardTitle(cylindersCount, "Cylinders", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getFirstAvailableValue(
																cylindersCount?.runningCount,
																cylindersCount?.running
															) ?? "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getFirstAvailableValue(
																cylindersCount?.hiddenCount,
																cylindersCount?.hide,
																cylindersCount?.close
															) ?? "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple2">
										<div className="card-content">
											<div className="kyc-card-body KYCBusinessOverview images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCBusinessOverviewImage} alt="Img" />
														<span>
															{getCardTitle(businessOverviewCount, "Business Overview", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{businessOverviewCount.running || "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{businessOverviewCount.hide || "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<Card className="kyc-table-card">
								<h5 className="kyc-company-title mb-4">Partner Function Detail</h5>
								<Tabs
									activeKey={activeTab}
									className="dash_tab_panel kyc_company_tabs"
									type="card"
									destroyInactiveTabPane={false}
									onChange={handleTabChange}>
									<TabPane tab="Partner Function" key="partnerFunction">
										<Table
											bordered
											scroll={{ x: 1400 }}
											className="logistic_tables kyc_logistic_tables"
											dataSource={paginatedPartnerFunctionRows}
											columns={tableColumns}
											onChange={handleTableAscDes}
											rowKey={(record, index) =>
												record.id || record.crmGuid || record.sapCode || index
											}
											locale={{ emptyText: "No data found" }}
											pagination={{
												total: sortedPartnerFunctionRows.length,
												current: partnerFunctionPagination.currentPage,
												pageSize: partnerFunctionPagination.pageSize,
												showSizeChanger: false,
												showTotal: formatRecordCount,
												responsive: true,
											}}
											loading={isLoading}
										/>
									</TabPane>
									<TabPane tab="Material" key="material" forceRender>
										<KycMaterialsTab
											navigationContext={{
												groupCompanyId: navContext.groupCompanyId,
												groupCompanyName: navContext.groupCompanyName,
												materialsIsplCustomer:
													navContext.materialsCustomer || soldToPartyName,
												activeCompanyTab: "materials",
											}}
											tabTableData={materialsTable}
											isLoading={isMaterialsLoading}
											tableSorter={materialsSorter}
											OnhandleTableSortColumn={handleMaterialsTableChange}
											emptyText="No data found"
										/>
									</TabPane>
									<TabPane tab="Quotation" key="quotation">
										<KycPartnerQuotationTab
											crmGuid={crmGuid}
											customer={materialsCustomerFilter}
											isActive={activeTab === "quotation"}
											navigationContext={{
												groupCompanyId: navContext.groupCompanyId,
												groupCompanyName: navContext.groupCompanyName,
												accountName: soldToPartyName,
												materialsCustomer:
													navContext.materialsCustomer || soldToPartyName,
												returnTab: navContext.returnTab,
											}}
										/>
									</TabPane>
									<TabPane tab="Orders" key="orders">
										<KycPartnerOrdersTab
											crmGuid={crmGuid}
											customer={materialsCustomerFilter}
											isActive={activeTab === "orders"}
											navigationContext={{
												groupCompanyId: navContext.groupCompanyId,
												groupCompanyName: navContext.groupCompanyName,
												accountName: soldToPartyName,
												materialsCustomer:
													navContext.materialsCustomer || soldToPartyName,
												returnTab: navContext.returnTab,
											}}
										/>
									</TabPane>
									<TabPane tab="Design" key="design">
										<KycPartnerDesignTab
											crmGuid={crmGuid}
											customer={materialsCustomerFilter}
											isActive={activeTab === "design"}
											navigationContext={{
												groupCompanyId: navContext.groupCompanyId,
												groupCompanyName: navContext.groupCompanyName,
												accountName: soldToPartyName,
												materialsCustomer:
													navContext.materialsCustomer || soldToPartyName,
												returnTab: navContext.returnTab,
											}}
										/>
									</TabPane>
									<TabPane tab="Credit Detail" key="creditDetail">
										<div className="kyc-tab-placeholder">
											Credit detail data will appear here once the API is ready.
										</div>
									</TabPane>
									<TabPane tab="Cardiogram" key="cardiogram">
										<div className="kyc-tab-placeholder">
											Cardiogram data will appear here once the API is ready.
										</div>
									</TabPane>
								</Tabs>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycPartnerFunctionDetails;
